// React
import React from 'react';

// Emotion
import { css } from '@emotion/react';

// Components
import GridCol from "@upsun/react-component-library/src/components/GridCol";
import GridContainer from "@upsun/react-component-library/src/components/GridContainer";
import Heading from "@upsun/react-component-library/src/components/Heading";
import YoutubeEmbed from '@upsun/react-component-library/src/components/YoutubeEmbed';

import Tags from './Tags';
import { BackLink } from './BackLink';

// Style utils
import { fontSizeLineHeight, fonts, mediaQueries } from "@upsun/react-component-library/src/styles/css-utils";
import { pxToRem } from "@upsun/react-component-library/src/styles/css-utils";

// Gatsby Imports
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { setImageSize } from '../utils/image-utils';
import { colors } from '../utils/colors';

type BlogHeroProps = {
  date : string,
  tags : string[],
  title : string,
  image : IGatsbyImageData,
  altText : string,
  video? : string,
  backLinkTxt : string,
  backLinkHref : string
};

export const BlogHero : React.FC<BlogHeroProps> = ({ date, tags, title, image, altText, video, backLinkTxt, backLinkHref, ...props }) => {
  const textColStyles = css`
    text-align: left;
    margin-bottom: ${pxToRem(24)};
    
    ${mediaQueries.md} {
      margin-bottom: ${pxToRem(40)};
    }

    ${mediaQueries.lg} {
      grid-row-start: 2;
      margin-top: 0;
    }
  `;

  const headingStyles = css`
    margin-bottom: ${pxToRem(40)};
  `;

  const tagStyles = css`
    margin-bottom: ${pxToRem(24)};
  `;

  const dateStyles = css`
    color: ${colors.mediumGray};
    font-family: "Space Grotesk", sans-serif;
    ${fontSizeLineHeight(14, 21.45)};
  `;

  const imgTxtStyles = css`
    align-self: center;
    margin-bottom: ${pxToRem(24)};
    
    ${mediaQueries.md} {
      margin-bottom: ${pxToRem(40)};
    }
  `;

  const backLinkStyles = css`
    margin-bottom: ${pxToRem(40)};

    ${mediaQueries.lg} {
      margin-top: ${pxToRem(48)};
    }
  `;

  const imgStyles = css`
    border-radius: ${pxToRem(16)};
  `;

  const mediaElement = image ? 
    <GatsbyImage 
      image={setImageSize(image, 'hero') as IGatsbyImageData} 
      alt={altText || ""} 
      loading={"eager"} 
      css={imgStyles}
    /> : '';
  
  return (
    <div {...props}>
      <GridContainer rowGap={false}>
        <GridCol colStart={1} colEnd={13} colStartMd={2}>
          <BackLink text={backLinkTxt} btnHref={backLinkHref} css={backLinkStyles} />
        </GridCol>
        <GridCol colStart={1} colEnd={13} colStartMd={2} colEndMd={12} colStartLg={7} css={imgTxtStyles}>
          {mediaElement}
        </GridCol>
        <GridCol colStart={1} colEnd={13} colStartMd={2} colEndMd={12} colEndLg={video || image ? 7 : 10} css={textColStyles}>
          <Heading level={1} levelStyle={2} text={title} css={headingStyles} />
          <Tags css={tagStyles} tags={tags} />
          <div css={dateStyles}>{date}</div>
        </GridCol>
      </GridContainer>
    </div>
  );
}